import React, { useState, useEffect } from 'react';

export function GalleryLightbox({ gallery, title, images = [] }) {
  const [selectedIndex, setSelectedIndex] = useState(null);

  // If a gallery object is passed from CMS
  const imageList = gallery?.images?.length ? gallery.images : images;

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (selectedIndex === null) return;
      if (e.key === 'Escape') setSelectedIndex(null);
      if (e.key === 'ArrowRight') nextImage();
      if (e.key === 'ArrowLeft') prevImage();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedIndex, imageList.length]);

  if (!imageList || imageList.length === 0) return null;

  const nextImage = () => {
    setSelectedIndex((prev) => (prev + 1) % imageList.length);
  };

  const prevImage = () => {
    setSelectedIndex((prev) => (prev - 1 + imageList.length) % imageList.length);
  };

  return (
    <div className="w-full">
      {title && (
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-xl font-serif font-bold text-brand-900">{title}</h3>
          <span className="text-xs font-semibold text-stone-500">
            {imageList.length} billeder
          </span>
        </div>
      )}

      {/* Responsive Gallery Grid */}
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 sm:gap-4">
        {imageList.map((imgUrl, index) => (
          <button
            key={index}
            type="button"
            onClick={() => setSelectedIndex(index)}
            className="group relative aspect-4/3 rounded-2xl overflow-hidden bg-sand-100 border border-sand-200 focus:outline-none focus:ring-2 focus:ring-accent-500 shadow-sm hover:shadow-md transition-all text-left"
            aria-label={`Åbn billede ${index + 1} i fuld skærm`}
          >
            <img
              src={`https://assets.auxodev.dk/cdn-cgi/image/width=600,quality=80,format=auto/${imgUrl.replace('https://assets.auxodev.dk/', '')}`}
              alt={gallery?.title || title || `Galleri billede ${index + 1}`}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
              loading="lazy"
            />
            <div className="absolute inset-0 bg-brand-950/20 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
              <div className="w-10 h-10 rounded-full bg-white/90 text-brand-900 flex items-center justify-center shadow-lg transform scale-75 group-hover:scale-100 transition-transform">
                <i className="fa-solid fa-magnifying-glass-plus" aria-hidden="true" />
              </div>
            </div>
          </button>
        ))}
      </div>

      {/* Lightbox Modal */}
      {selectedIndex !== null && (
        <div 
          className="fixed inset-0 z-50 bg-black/90 backdrop-blur-md flex items-center justify-center p-4 sm:p-8 animate-in fade-in duration-200"
          onClick={() => setSelectedIndex(null)}
        >
          {/* Close button */}
          <button
            type="button"
            onClick={() => setSelectedIndex(null)}
            className="absolute top-4 right-4 z-50 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center text-xl transition-colors focus:outline-none"
            aria-label="Luk fuldskærmsvisning"
          >
            <i className="fa-solid fa-xmark" aria-hidden="true" />
          </button>

          {/* Prev button */}
          {imageList.length > 1 && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                prevImage();
              }}
              className="absolute left-4 top-1/2 -translate-y-1/2 z-50 w-12 h-12 rounded-full bg-white/10 hover:bg-white/25 text-white flex items-center justify-center text-lg transition-colors focus:outline-none"
              aria-label="Forrige billede"
            >
              <i className="fa-solid fa-chevron-left" aria-hidden="true" />
            </button>
          )}

          {/* Active Image container */}
          <div 
            className="relative max-w-5xl max-h-[85vh] flex flex-col items-center justify-center"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={`https://assets.auxodev.dk/cdn-cgi/image/width=1600,quality=90,format=auto/${imageList[selectedIndex].replace('https://assets.auxodev.dk/', '')}`}
              alt={gallery?.title || `Billede ${selectedIndex + 1}`}
              className="max-h-[75vh] w-auto object-contain rounded-xl shadow-2xl"
            />

            {/* Caption & Counter */}
            <div className="mt-4 flex items-center justify-between w-full text-white/80 text-xs sm:text-sm px-2">
              <span className="font-serif">
                {gallery?.title || title || 'Fodtid Galleri'}
              </span>
              <span>
                {selectedIndex + 1} / {imageList.length}
              </span>
            </div>
          </div>

          {/* Next button */}
          {imageList.length > 1 && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                nextImage();
              }}
              className="absolute right-4 top-1/2 -translate-y-1/2 z-50 w-12 h-12 rounded-full bg-white/10 hover:bg-white/25 text-white flex items-center justify-center text-lg transition-colors focus:outline-none"
              aria-label="Næste billede"
            >
              <i className="fa-solid fa-chevron-right" aria-hidden="true" />
            </button>
          )}
        </div>
      )}
    </div>
  );
}
